import React, { useState, useRef, useEffect } from "react";
import "./IntroOverlay.css";

const IntroOverlay = ({ onComplete }) => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [needsUserInteraction, setNeedsUserInteraction] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Try playing with audio enabled first
    video.muted = false;
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsMuted(false);
        })
        .catch((error) => {
          console.warn(
            "Autoplay with audio was restricted by browser policy. Falling back to muted autoplay.",
            error
          );
          // Fall back to muted autoplay
          video.muted = true;
          setIsMuted(true);
          video.play().catch(() => {
            // If even muted autoplay is blocked, prompt user for interaction
            setNeedsUserInteraction(true);
          });
        });
    }
  }, []);

  const handleFinish = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      if (onComplete) onComplete();
    }, 600);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      const nextMuted = !videoRef.current.muted;
      videoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
    }
  };

  const handleStartPlay = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      setIsMuted(false);
      videoRef.current
        .play()
        .then(() => {
          setNeedsUserInteraction(false);
        })
        .catch((err) => {
          console.error("Playback failed:", err);
        });
    }
  };

  return (
    <div className={`intro-overlay ${isFadingOut ? "fade-out" : ""}`}>
      <div className="intro-video-container">
        <video
          ref={videoRef}
          src="/intro.mp4"
          className="intro-video"
          playsInline
          onEnded={handleFinish}
          onClick={(e) => e.stopPropagation()}
        />

        {needsUserInteraction && (
          <div className="intro-start-prompt" onClick={handleStartPlay}>
            <button className="intro-play-btn">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
              <span>Click to Enter</span>
            </button>
          </div>
        )}

        <div className="intro-controls">
          <button
            className="intro-btn sound-btn"
            onClick={toggleMute}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M9 9v6a3 3 0 0 0 5.12 2.12M15 9.34V4a2 2 0 0 0-3.54-1.3L7.8 6H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h3.8l1.45 1.16"></path>
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
              </svg>
            )}
            <span>{isMuted ? "Unmute" : "Sound On"}</span>
          </button>

          <button className="intro-btn skip-btn" onClick={handleFinish}>
            <span>Skip Intro</span>
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="5 4 15 12 5 20 5 4"></polygon>
              <line x1="19" y1="5" x2="19" y2="19"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntroOverlay;
